import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Settings } from "lucide-react";
import { formatPhoneNumber } from "@/lib/phoneUtils";

interface AddEmployeeSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddEmployeeSheet({ isOpen, onClose }: AddEmployeeSheetProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
    hireDate: "",
    status: "active" as "active" | "inactive",
  });

  const createEmployee = useMutation(api.employees.create);
  const positions = useQuery(api.positions.listEnabled);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData((prev) => ({ ...prev, phone: formattedPhone }));
  };

  const handleSubmit = async () => {
    try {
      await createEmployee({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        position: formData.position,
        department: formData.department,
        hireDate: formData.hireDate,
        status: formData.status,
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        position: "",
        department: "",
        hireDate: "",
        status: "active",
      });

      onClose();
    } catch (error) {
      console.error("Failed to create employee:", error);
    }
  };
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add New Employee</SheetTitle>
          <SheetDescription>Enter the details for the new team member.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                placeholder="Enter employee name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="position">Position</Label>
                  {positions && positions.length === 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      onClick={() => {
                        onClose();
                        navigate("/settings?tab=positions");
                      }}
                    >
                      <Settings className="mr-1 h-3 w-3" />
                      Configure Positions
                    </Button>
                  )}
                </div>
                <Select
                  value={formData.position}
                  onValueChange={(value) => handleInputChange("position", value)}
                  disabled={positions === undefined}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        positions === undefined
                          ? "Loading positions..."
                          : positions.length === 0
                            ? "No positions configured"
                            : "Select position"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {positions && positions.length > 0 ? (
                      positions.map((position: any) => (
                        <SelectItem key={position._id} value={position.name}>
                          {position.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="__no_positions__" disabled>
                        No positions available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select
                  value={formData.department}
                  onValueChange={(value) => handleInputChange("department", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Operations">Operations</SelectItem>
                    <SelectItem value="Cleaning Staff">Cleaning Staff</SelectItem>
                    <SelectItem value="Human Resources">Human Resources</SelectItem>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                    <SelectItem value="Management">Management</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="(555) 555-5555"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hire-date">Hire Date</Label>
              <Input
                id="hire-date"
                type="date"
                value={formData.hireDate}
                onChange={(e) => handleInputChange("hireDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              className="w-full"
              onClick={handleSubmit}
              disabled={
                !formData.name ||
                !formData.email ||
                !formData.phone ||
                !formData.position ||
                !formData.department ||
                !formData.hireDate
              }
            >
              Add Employee
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
